/**
 * Sprint 144 — **Leer una cantidad de dinero escrita por una persona.**
 *
 * ## El defecto que lo obliga a existir
 *
 * La pantalla del cierre leía el efectivo contado con `Number(texto.replace(',', '.'))`, y
 * cualquier fallo caía en `|| 0`. Lo cazó la auditoría de interfaz, con el caso que más duele:
 *
 * | Lo que teclea | Lo que entendía | Lo que habría pasado |
 * |---|---|---|
 * | `2,700` | **$2.70** | corte cerrado declarando $2.70 y un faltante falso de $2,782.30 |
 * | `2,785.00` | **$0.00** | *"Falta $2,785.00"* sobre una caja que cuadraba |
 * | `2 700` | **$0.00** | lo mismo |
 *
 * Y no es un error rebuscado: **la propia pantalla muestra "$2,785.00" con su coma**, así que
 * escribirla es lo natural. El cierre del corte no tiene vuelta atrás y ese número sale impreso.
 *
 * ## Las reglas, y por qué
 *
 * Se acepta lo que una persona escribe de verdad: con o sin símbolo de moneda, con espacios, con
 * coma de millar (`2,785.00`) o al revés (`2.785,00`). **El último separador manda** si lleva uno
 * o dos dígitos detrás —es el decimal—; con tres dígitos detrás es separador de millar. Es la
 * regla que funciona en las dos escrituras sin preguntarle a nadie de qué país es.
 *
 * Y lo que no se entiende **no se convierte en cero**: devuelve un error para que la pantalla lo
 * diga y no deje cerrar. Un cero silencioso sobre dinero es peor que un campo vacío.
 */

export type DineroTecleado =
  | { ok: true; centavos: number }
  | { ok: false; motivo: 'vacio' | 'no_es_numero' };

/** Quita lo que una persona escribe alrededor del número: `$`, espacios (incluido el fino). */
function limpiar(texto: string): string {
  return texto.replace(/[$\s  ]/g, '');
}

export function leerDineroTecleado(texto: string): DineroTecleado {
  const limpio = limpiar(texto);
  if (limpio === '') return { ok: false, motivo: 'vacio' };

  // Sólo dígitos y separadores. Un signo menos no tiene lectura: en la gaveta no hay dinero negativo.
  if (!/^[\d.,]+$/.test(limpio)) return { ok: false, motivo: 'no_es_numero' };

  const ultimaComa = limpio.lastIndexOf(',');
  const ultimoPunto = limpio.lastIndexOf('.');
  const posSeparador = Math.max(ultimaComa, ultimoPunto);

  let entero = limpio;
  let decimales = '';

  if (posSeparador !== -1) {
    const detras = limpio.length - posSeparador - 1;
    /*
      Uno o dos dígitos detrás: es el decimal. Tres: es separador de millar (`2,700` son dos mil
      setecientos pesos, no dos con setenta). Cualquier otra cosa —`2,7000`— no es una cantidad
      que alguien haya querido escribir.
    */
    if (detras === 1 || detras === 2) {
      entero = limpio.slice(0, posSeparador);
      decimales = limpio.slice(posSeparador + 1);
    } else if (detras === 3) {
      entero = limpio;
    } else {
      return { ok: false, motivo: 'no_es_numero' };
    }
  }

  // Lo que quede en la parte entera sólo puede llevar separadores de millar.
  const soloDigitos = entero.replace(/[.,]/g, '');
  if (soloDigitos === '' || !/^\d+$/.test(soloDigitos)) {
    return { ok: false, motivo: 'no_es_numero' };
  }
  /*
    Los grupos de millar tienen que ser de tres dígitos. `1,23,456` no es una cifra escrita por
    alguien que sepa cuánto tiene en la caja: es un error de tecleo, y se dice.
  */
  const grupos = entero.split(/[.,]/);
  if (grupos.length > 1) {
    const malFormado =
      grupos[0].length === 0 ||
      grupos[0].length > 3 ||
      grupos.slice(1).some((g) => g.length !== 3);
    if (malFormado) return { ok: false, motivo: 'no_es_numero' };
  }

  const centavos = Number(soloDigitos) * 100 + Number(decimales.padEnd(2, '0'));
  if (!Number.isFinite(centavos) || !Number.isSafeInteger(centavos)) {
    return { ok: false, motivo: 'no_es_numero' };
  }
  return { ok: true, centavos };
}
